<template>
  <div class="p-6 pt-0">
    <HLSpace vertical :size="24">
      <!-- Filters -->
        <SectionFilter
        title="All Bugs"
        :show-search="false"
        :active-filter-count="activeFiltersCount"
        :has-active-filters="activeFiltersCount > 0"
        :show-export="false"
        :show-filters="true"
        :expanded="showFilters"
        :filtered-count="filteredBugs.length"
        :total-count="bugs.filter(b => !b.is_deleted && !b.isDeleted).length"
        show-deleted-toggle
        :include-deleted="showDeleted"
        @toggle="showFilters = !showFilters"
        @clear="clearAllFilters"
        @update:include-deleted="handleIncludeDeletedToggle"
      >
        <template #actions="{ defaults }">
        <HLButton
          id="bugs-refresh"
            v-bind="defaults"
          @click="refreshBugs"
          :disabled="loading"
          variant="secondary"
          color="gray"
        >
          <template #iconLeft>
              <Icon name="refresh" size="sm" :class="{ 'animate-spin': loading }" />
          </template>
          Refresh
        </HLButton>
          
          <HLButton
            id="bugs-toggle-deleted"
            v-bind="defaults"
            @click="handleIncludeDeletedToggle(!showDeleted)"
            variant="secondary"
            color="gray"
          >
            <template #iconLeft>
              <Icon name="archive" size="sm" />
            </template>
            {{ showDeleted ? 'Hide Deleted' : 'View Deleted' }}
        </HLButton>
        
        <HLButton
          v-if="can.createBugs()"
          id="bugs-create"
            v-bind="defaults"
          variant="primary"
          color="blue"
            @click="createBug"
            :disabled="showDeleted"
        >
          <template #iconLeft>
              <Icon name="plus" size="sm" />
          </template>
          Create Bug
        </HLButton>
        </template>
        
        <template #default>
          <!-- Search Bar -->
          <div class="col-span-full mb-4">
            <HLInput
              v-model="filters.search"
              placeholder="Search bugs by title, description, or ID..."
              clearable
              class="w-full"
            >
              <template #prefix>
                <Icon name="search" size="sm" class="text-gray-400" />
              </template>
            </HLInput>
      </div>

          <!-- Filters content -->
          <div class="col-span-full">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <!-- Project Filter -->
              <div>
                <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Project</HLText>
                <HLSelect
                  :id="`filter-project-${uniqueFilterId}`"
                  v-model:value="filters.project"
                  :options="projectSelectOptions"
                  placeholder="All Projects"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
      </div>

              <!-- Status Filter -->
              <div>
                <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Status</HLText>
                <HLSelect
                  :id="`filter-status-${uniqueFilterId}`"
                  v-model:value="filters.status"
                  :options="statusSelectOptions"
                  placeholder="All Status"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
      </div>

              <!-- Priority Filter -->
        <div>
                <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Priority</HLText>
                <HLSelect
                  :id="`filter-priority-${uniqueFilterId}`"
                  v-model:value="filters.priority"
                  :options="prioritySelectOptions"
                  placeholder="All Priorities"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
        </div>
        
              <!-- Severity Filter -->
        <div>
                <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Severity</HLText>
                <HLSelect
                  :id="`filter-severity-${uniqueFilterId}`"
                  v-model:value="filters.severity"
                  :options="severitySelectOptions"
                  placeholder="All Severities"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
        </div>
        
              <!-- Assigned To Filter -->
        <div>
                <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Assigned To</HLText>
                <HLSelect
                  :id="`filter-assignedTo-${uniqueFilterId}`"
                  v-model:value="filters.assignedTo"
                  :options="assignedToSelectOptions"
                  placeholder="All Users"
                  multiple
                  filterable
                  clearable
                  :option-renderer="optionRenderer"
                />
        </div>
        
              <!-- Created By Filter -->
        <div>
                <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Created By</HLText>
                <HLSelect
                  :id="`filter-createdBy-${uniqueFilterId}`"
                  v-model:value="filters.createdBy"
                  :options="createdBySelectOptions"
                  placeholder="All Creators"
                  multiple
                  filterable
                  clearable
                  :option-renderer="optionRenderer"
                />
        </div>
        
              <!-- Feature Filter -->
        <div>
                <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Feature</HLText>
                <FeatureTreeSelect
                  :id="`filter-feature-${uniqueFilterId}`"
                  v-model="filters.feature"
                  :features="features"
                  placeholder="All Features"
                  :multiple="true"
                  :filterable="true"
                />
                <!-- Include Sub-tasks Checkbox -->
                <div class="mt-2">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      :id="`include-subtasks-${uniqueFilterId}`"
                      v-model="includeSubTasks"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span class="text-sm text-gray-700 dark:text-gray-300">Include Sub-tasks</span>
                  </label>
                </div>
        </div>
            </div>
          </div>
        </template>
      </SectionFilter>

      <!-- Bugs Table -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border-t border-gray-200 dark:border-gray-700">
        <div v-if="loading" class="flex flex-col items-center justify-center py-12">
          <HLSpin id="bugs-list-loading-spinner" size="lg" :show="loading">
            <template #description>
              <HLText variant="body-sm" color="muted">Loading bugs...</HLText>
            </template>
          </HLSpin>
        </div>
        
        <div v-else>
          <!-- Empty State (Outside Table) -->
          <div v-if="paginatedBugs.length === 0 && !loading" class="border border-gray-200 dark:border-gray-700 rounded-lg p-24">
            <div class="flex items-center justify-center w-full">
              <div class="text-center max-w-md mx-auto">
                <div class="mb-8 flex justify-center">
                  <Icon name="bug" size="xl" :style="{ color: TAB_ICON_COLORS.BUGS, opacity: 0.7 }" />
        </div>
        
                <!-- No Data At All -->
                <div v-if="bugs.length === 0 && !showDeleted">
                  <Heading level="h5" class="my-2 text-gray-900 dark:text-white">
                    No Bugs Found
                  </Heading>
                  <Text variant="body-sm" color="muted" class="mt-3 mb-6">
                    There are no bugs yet. Create one to get started!
                  </Text>
                  <div class="flex justify-center mt-6">
                    <HLButton 
                      v-if="can.createBugs()"
                      id="bugs-empty-create" 
                      variant="primary" 
                      color="blue" 
                      @click="createBug"
                      size="md"
                    >
                      <template #iconLeft>
                        <Icon name="plus" size="sm" />
                      </template>
                      Create Bug
                    </HLButton>
                  </div>
        </div>
        
                <!-- No Deleted Bugs -->
                <div v-else-if="bugs.filter(b => b.is_deleted || b.isDeleted).length === 0 && showDeleted">
                  <Heading level="h5" class="my-2 text-gray-900 dark:text-white">
                    No Deleted Bugs
                  </Heading>
                  <Text variant="body-sm" color="muted" class="mt-3 mb-6">
                    No deleted bugs to display
                  </Text>
        </div>
        
                <!-- No Matching Filter Results -->
                <div v-else>
                  <Heading level="h5" class="my-2 text-gray-900 dark:text-white">
                    No Results Found
                  </Heading>
                  <Text variant="body-sm" color="muted" class="mt-3 mb-6">
                    No bugs match your filters. Try adjusting your search criteria.
                  </Text>
                  <HLButton 
                    v-if="activeFiltersCount > 0"
                    id="bugs-empty-clear-filters" 
                    variant="secondary" 
                    color="gray" 
                    @click="clearAllFilters"
                    size="sm"
                  >
                    Clear Filters
                  </HLButton>
        </div>
      </div>
    </div>
      </div>

          <!-- Data Table (Only show when there's data) -->
          <div v-else>
            <HLDataTableWrapper responsive-column-width max-width="100%" v-bind="tableMaxHeight ? { 'max-height': tableMaxHeight } : {}">
              <HLDataTable
                ref="tableInstance"
                id="bugs-table"
                :columns="dataTableColumns"
                :data="paginatedBugs"
                :loading="loading"
                row-key="id"
                :column-hover="false"
                :horizontal-borders="true"
                :pagination="false"
                :page-size="pageSize"
                class="bugs-data-table"
              >
                <!-- Empty State Template -->
                <template #empty>
                  <div class="flex items-center justify-center w-full py-16">
                    <div class="text-center max-w-md mx-auto px-4">
                      <div class="mb-6">
                        <Icon name="bug" size="xxl" :style="{ color: TAB_ICON_COLORS.BUGS, opacity: 0.7 }" />
                </div>
                      
                      <!-- No Data At All -->
                      <div v-if="bugs.length === 0 && !showDeleted">
                        <Heading level="h5" class="my-2 text-gray-900 dark:text-white">
                          No Bugs Found
                        </Heading>
                        <Text variant="body-sm" color="muted" class="mt-3 mb-6">
                          There are no bugs yet. Create one to get started!
                        </Text>
                        <div class="flex justify-center mt-4">
                          <HLButton 
                            v-if="can.createBugs()"
                            id="bugs-empty-table-create" 
                            variant="primary" 
                            color="blue" 
                            @click="createBug"
                            size="md"
                          >
                            <template #iconLeft>
                              <Icon name="plus" size="sm" />
                            </template>
                            Create Bug
                          </HLButton>
                </div>
                </div>
                      
                      <!-- No Deleted Bugs -->
                      <template v-else-if="bugs.filter(b => b.is_deleted || b.isDeleted).length === 0 && showDeleted">
                        <Heading level="h5" class="my-2 text-gray-900 dark:text-white">
                          No Deleted Bugs
                        </Heading>
                        <Text variant="body-sm" color="muted" class="mt-3 mb-6">
                          No deleted bugs to display
                        </Text>
                      </template>
                      
                      <!-- No Matching Filter Results -->
                      <template v-else>
                        <Heading level="h5" class="my-2 text-gray-900 dark:text-white">
                          No Results Found
                        </Heading>
                        <Text variant="body-sm" color="muted" class="mt-3 mb-6">
                          No bugs match your filters. Try adjusting your search criteria.
                        </Text>
                <HLButton
                          v-if="activeFiltersCount > 0"
                          id="bugs-empty-clear-filters" 
                          variant="secondary" 
                          color="gray" 
                          @click="clearAllFilters"
                  size="sm"
                >
                          Clear Filters
                </HLButton>
                      </template>
      </div>
    </div>
                </template>
              </HLDataTable>
            </HLDataTableWrapper>
          </div>

          <!-- Pagination inside table border -->
          <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700" v-if="filteredBugs.length > 0">
            <div class="flex justify-end">
              <HLPagination
                :current-page="currentPage"
                :item-count="filteredBugs.length"
                :per-page="String(pageSize)"
                :pages-to-display="7"
                per-page-text="Rows per page"
                :per-page-dropdown-options="perPageOptions"
                @update:page="handlePageChange"
                @update:perPage="handlePerPageChange"
                size="md"
              >
                <template #prev>Previous</template>
                <template #next>Next</template>
              </HLPagination>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Delete Bug Modal -->
      <ConfirmationDialog
        v-if="showDeleteModal"
        :is-open="showDeleteModal"
        title="Delete Bug"
        description="Are you sure you want to delete this bug?"
        confirm-text="Delete"
        cancel-text="Cancel"
        icon-type="danger"
        @confirm="confirmDeleteBug"
        @cancel="showDeleteModal = false"
      />
      
      <!-- Restore Bug Modal -->
      <ConfirmationDialog
        v-if="showRestoreModal"
        :is-open="showRestoreModal"
        title="Restore Bug"
        description="Are you sure you want to restore this bug?"
        confirm-text="Restore"
        cancel-text="Cancel"
        icon-type="success"
        @confirm="confirmRestoreBug"
        @cancel="showRestoreModal = false"
      />
    
      <!-- Project Selector Modal -->
      <ProjectSelectorModal
        :is-open="showProjectSelector"
        title="Select a Project"
        description="Choose a project to create a bug in"
        :projects="projects"
        @confirm="handleProjectSelected"
        @cancel="showProjectSelector = false"
      />
      
      <!-- Single Clone Bug Modal -->
      <ConfirmationDialog
        v-if="showSingleCloneModal"
        :is-open="showSingleCloneModal"
        title="Clone Bug"
        description="Are you sure you want to clone this bug? A new bug will be created with the same details."
        confirm-text="Clone"
        cancel-text="Cancel"
        icon-type="info"
        custom-icon="copy"
        confirm-variant="primary"
        :loading="cloning"
        @confirm="confirmSingleCloneBug"
        @cancel="showSingleCloneModal = false"
      />
    </HLSpace>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onActivated, nextTick, h } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { usePermissions } from '@/composables/usePermissions'
import { useAuthStore } from '@/stores/auth'
import { bugService, projectService, userService } from '@/database/services'
import { FILTER_VALUES } from '@/constants/reportConstants'
import { ACTION_ICON_COLORS } from '@/constants/ui'
import { TAB_ICON_COLORS } from '@/constants/projects'
import {
  BUG_STATUS_OPTIONS,
  BUG_PRIORITY_OPTIONS,
  BUG_TYPE_OPTIONS,
  BUG_SEVERITY_OPTIONS
} from '@/constants/bugs'
import { formatDate } from '@/utils/dateUtils'
import { SELECT_ALL_VALUE, normalizeSelectAllSelection } from '@/utils/selectAll'
import SectionFilter from '@/components/reports/SectionFilter.vue'
import { HLButton, HLSpace, HLSelect, HLInput, HLText, HLDataTable, HLDataTableWrapper, HLPagination, HLTag, HLPopover, HLSpin } from '@platform-ui/highrise'
import Icon from '@/components/ui/Icon.vue'
import ConfirmationDialog from '@/components/ui/ConfirmationDialog.vue'
import Heading from '@/components/ui/Heading.vue'
import Text from '@/components/ui/Text.vue'
import ProjectSelectorModal from '@/components/ui/ProjectSelectorModal.vue'
import FeatureTreeSelect from '@/components/ui/FeatureTreeSelect.vue'

const router = useRouter()
const toast = useToast()
const { can } = usePermissions()
const authStore = useAuthStore()

// Props & State
const bugs = ref([])
const users = ref([])
const projects = ref([])
const features = ref([])
const loading = ref(false)
const showFilters = ref(false)
const showDeleted = ref(false)
const includeSubTasks = ref(false)
const showDeleteModal = ref(false)
const showRestoreModal = ref(false)
const showProjectSelector = ref(false)
const showSingleCloneModal = ref(false)
const selectedBugId = ref(null)
const selectedSingleBugId = ref(null)
const cloning = ref(false)

// Pagination
const currentPage = ref(1)
const pageSize = ref(10)

// Per page options for pagination
const perPageOptions = [
  { key: '10', label: '10' },
  { key: '20', label: '20' },
  { key: '50', label: '50' },
  { key: '100', label: '100' }
]

// Filters
const filters = ref({
  search: '',
  project: [],
  status: [],
  priority: [],
  severity: [],
  assignedTo: [],
  createdBy: [],
  feature: []
})

// Unique filter ID for accessibility
const uniqueFilterId = ref(Math.random().toString(36).substr(2, 9))

// Helper to create option with checkbox rendering
const createOptionWithCheckbox = (opt, getSelectedValues) => {
  return {
    label: opt.label,
    value: opt.value,
    renderOption: () => {
      const selectedValues = getSelectedValues() || []
      const isSelected =
        Array.isArray(selectedValues) &&
        (selectedValues.includes(opt.value) || selectedValues.includes(SELECT_ALL_VALUE))
      return h('div', { 
        class: 'flex items-center gap-1.5 w-full cursor-pointer px-2 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-700'
      }, [
        h('input', {
          type: 'checkbox',
          checked: isSelected,
          class: 'rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer flex-shrink-0',
          style: 'pointer-events: none;',
          readonly: true
        }),
        h('span', { 
          class: 'text-sm text-gray-900 dark:text-white select-none flex-1'
        }, opt.label)
      ])
    }
  }
}

// "Select All" option helper (for multi-select filter dropdowns)
const createSelectAllOptionWithCheckbox = (getSelectedValues, getAllValues) => {
  return {
    label: 'Select All',
    value: SELECT_ALL_VALUE,
    renderOption: () => {
      const rawSelectedValues = getSelectedValues() || []
      const hasSelectAll = Array.isArray(rawSelectedValues) && rawSelectedValues.includes(SELECT_ALL_VALUE)
      const selectedValues = Array.isArray(rawSelectedValues)
        ? rawSelectedValues.filter(v => v !== SELECT_ALL_VALUE)
        : []
      const allValues = getAllValues() || []
      const isAllSelected = hasSelectAll || (allValues.length > 0 && allValues.every(v => selectedValues.includes(v)))
      return h('div', { 
        class: 'flex items-center gap-1.5 w-full cursor-pointer px-2 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-700'
      }, [
        h('input', {
          type: 'checkbox',
          checked: isAllSelected,
          class: 'rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer flex-shrink-0',
          style: 'pointer-events: none;',
          readonly: true
        }),
        h('span', { 
          class: 'text-sm font-medium text-gray-900 dark:text-white select-none flex-1'
        }, 'Select All')
      ])
    }
  }
}

// Option renderer function for HLSelect
const optionRenderer = (option) => {
  if (option.renderOption) {
    return option.renderOption()
  }
  return h('span', option.label)
}

// Computed Options for Filters
const projectOptions = computed(() => 
  projects.value.map(p => ({
    value: p.id,
    label: p.name
  }))
)

const statusOptions = computed(() => BUG_STATUS_OPTIONS)
const priorityOptions = computed(() => BUG_PRIORITY_OPTIONS)
const severityOptions = computed(() => BUG_SEVERITY_OPTIONS)

const assignedToOptions = computed(() => {
  const usersList = users.value.map(user => ({
    value: user.id,
    label: user.name || user.email
  }))
  return [
    { value: FILTER_VALUES.UNASSIGNED, label: 'Unassigned' },
    ...usersList
  ]
})

const createdByOptions = computed(() => {
  // In our DB, bugs.created_by is stored as the canonical user.id (user_...).
  return users.value
    .filter(user => user?.id)
    .map(user => ({
      value: user.id,
      label: user.name || user.email
    }))
})

// Convert options to HLSelect format with checkbox rendering
const projectSelectOptions = computed(() => {
  return [
    createSelectAllOptionWithCheckbox(
      () => filters.value.project,
      () => projectOptions.value.map(o => o.value)
    ),
    ...projectOptions.value.map(opt => createOptionWithCheckbox(opt, () => filters.value.project))
  ]
})

const statusSelectOptions = computed(() => {
  return [
    createSelectAllOptionWithCheckbox(
      () => filters.value.status,
      () => statusOptions.value.map(o => o.value)
    ),
    ...statusOptions.value.map(opt => createOptionWithCheckbox(opt, () => filters.value.status))
  ]
})

const prioritySelectOptions = computed(() => {
  return [
    createSelectAllOptionWithCheckbox(
      () => filters.value.priority,
      () => priorityOptions.value.map(o => o.value)
    ),
    ...priorityOptions.value.map(opt => createOptionWithCheckbox(opt, () => filters.value.priority))
  ]
})

const severitySelectOptions = computed(() => {
  return [
    createSelectAllOptionWithCheckbox(
      () => filters.value.severity,
      () => severityOptions.value.map(o => o.value)
    ),
    ...severityOptions.value.map(opt => createOptionWithCheckbox(opt, () => filters.value.severity))
  ]
})

const assignedToSelectOptions = computed(() => {
  return [
    createSelectAllOptionWithCheckbox(
      () => filters.value.assignedTo,
      () => assignedToOptions.value.map(o => o.value)
    ),
    ...assignedToOptions.value.map(opt => createOptionWithCheckbox(opt, () => filters.value.assignedTo))
  ]
})

const createdBySelectOptions = computed(() => {
  return [
    createSelectAllOptionWithCheckbox(
      () => filters.value.createdBy,
      () => createdByOptions.value.map(o => o.value)
    ),
    ...createdByOptions.value.map(opt => createOptionWithCheckbox(opt, () => filters.value.createdBy))
  ]
})

// Handle "Select All" toggling for filter multi-selects
watch(() => filters.value.project, (newVal) => {
  if (Array.isArray(newVal) && newVal.includes(SELECT_ALL_VALUE)) {
    filters.value.project = normalizeSelectAllSelection(newVal, projectOptions.value.map(o => o.value))
  }
})
watch(() => filters.value.status, (newVal) => {
  if (Array.isArray(newVal) && newVal.includes(SELECT_ALL_VALUE)) {
    filters.value.status = normalizeSelectAllSelection(newVal, statusOptions.value.map(o => o.value))
  }
})
watch(() => filters.value.priority, (newVal) => {
  if (Array.isArray(newVal) && newVal.includes(SELECT_ALL_VALUE)) {
    filters.value.priority = normalizeSelectAllSelection(newVal, priorityOptions.value.map(o => o.value))
  }
})
watch(() => filters.value.severity, (newVal) => {
  if (Array.isArray(newVal) && newVal.includes(SELECT_ALL_VALUE)) {
    filters.value.severity = normalizeSelectAllSelection(newVal, severityOptions.value.map(o => o.value))
  }
})
watch(() => filters.value.assignedTo, (newVal) => {
  if (Array.isArray(newVal) && newVal.includes(SELECT_ALL_VALUE)) {
    filters.value.assignedTo = normalizeSelectAllSelection(newVal, assignedToOptions.value.map(o => o.value))
  }
})
// Prevent "Select All" race condition when users/createdByOptions haven't loaded yet.
const pendingSelectAllCreatedBy = ref(false)
watch(() => filters.value.createdBy, (newVal) => {
  if (!Array.isArray(newVal)) return
  if (!newVal.includes(SELECT_ALL_VALUE)) {
    pendingSelectAllCreatedBy.value = false
    return
  }

  const allValues = createdByOptions.value.map(o => o.value).filter(Boolean)
  if (allValues.length === 0) {
    pendingSelectAllCreatedBy.value = true
    return
  }
  pendingSelectAllCreatedBy.value = false
  filters.value.createdBy = normalizeSelectAllSelection(newVal, allValues)
})

watch(createdByOptions, (opts) => {
  if (!pendingSelectAllCreatedBy.value) return
  const allValues = (opts || []).map(o => o.value).filter(Boolean)
  if (allValues.length === 0) return
  filters.value.createdBy = allValues
  pendingSelectAllCreatedBy.value = false
})
// If user clicked "Select All" before users loaded, we keep the sentinel and do nothing else.

// Feature Options
const featureOptions = computed(() => {
  // Include all features (parent features and sub-tasks)
  // Sort by featureId or title for better UX
  const sortedFeatures = [...features.value].sort((a, b) => {
    const aLabel = (a.featureId || a.title || '').toLowerCase()
    const bLabel = (b.featureId || b.title || '').toLowerCase()
    return aLabel.localeCompare(bLabel)
  })
  
  return sortedFeatures.map(feature => ({
    value: feature.id,
    label: feature.featureId ? `${feature.featureId} ${feature.title || ''}`.trim() : (feature.title || feature.id)
  }))
})

const featureSelectOptions = computed(() => {
  return featureOptions.value.map(opt => createOptionWithCheckbox(opt, () => filters.value.feature))
})

// Active Filters Count
const activeFiltersCount = computed(() => {
  let count = 0
  if (filters.value.project.length > 0) count++
  if (filters.value.status.length > 0) count++
  if (filters.value.priority.length > 0) count++
  if (filters.value.severity.length > 0) count++
  if (filters.value.assignedTo.length > 0) count++
  if (filters.value.createdBy.length > 0) count++
  if (filters.value.feature.length > 0) count++
  return count
})

// Filtered Bugs
const filteredBugs = computed(() => {
  let result = bugs.value.filter(bug => {
    // Filter by deleted status
    const isDeleted = bug.isDeleted || bug.is_deleted
    if (showDeleted.value && !isDeleted) return false
    if (!showDeleted.value && isDeleted) return false
    
    // Search filter
    if (filters.value.search) {
      const searchLower = filters.value.search.toLowerCase()
      const matchesTitle = bug.title?.toLowerCase().includes(searchLower)
      const matchesDescription = bug.description?.toLowerCase().includes(searchLower)
      const matchesBugId = bug.bugId?.toLowerCase().includes(searchLower)
      const matchesId = bug.id?.toLowerCase().includes(searchLower)
      if (!matchesTitle && !matchesDescription && !matchesBugId && !matchesId) return false
    }
    
    // Project filter
    if (filters.value.project.length > 0 && !filters.value.project.includes(bug.project_id)) return false
    
    // Status filter
    if (filters.value.status.length > 0 && !filters.value.status.includes(bug.status)) return false
    
    // Priority filter
    if (filters.value.priority.length > 0 && !filters.value.priority.includes(bug.priority)) return false
    
    // Severity filter
    if (filters.value.severity.length > 0 && !filters.value.severity.includes(bug.severity)) return false
    
    // Assigned To filter (OR logic: unassigned OR assigned to selected users)
    if (filters.value.assignedTo.length > 0) {
      const hasUnassigned = filters.value.assignedTo.includes(FILTER_VALUES.UNASSIGNED)
      const isUnassigned = !bug.assigned_to
      const assignedToSelectedUser = bug.assigned_to && filters.value.assignedTo.includes(bug.assigned_to)
      
      // Show bug if:
      // 1. "Unassigned" is selected AND bug is unassigned, OR
      // 2. Bug is assigned to one of the selected users
      const shouldInclude = (hasUnassigned && isUnassigned) || assignedToSelectedUser
      
      if (!shouldInclude) {
        return false
      }
    }

    // Created By filter
    if (filters.value.createdBy.length > 0) {
      const creatorId = bug.created_by || bug.createdBy
      if (!creatorId) return false
      
      // created_by stored in DB as canonical user.id
      if (!filters.value.createdBy.includes(creatorId)) return false
    }
    
    // Feature filter (with optional sub-tasks inclusion)
    if (filters.value.feature.length > 0) {
      const bugFeatureIds = bug.feature_ids || []
      
      // If bug has no feature_ids, exclude it
      if (!bugFeatureIds || bugFeatureIds.length === 0) {
        return false
      }
      
      // Get all feature IDs to check (both database IDs and featureIds)
      const featureIdsToCheck = new Set()
      const featureFeatureIdsToCheck = new Set() // For featureId matching (F00051, etc.)
      
      filters.value.feature.forEach(selectedFeatureId => {
        // selectedFeatureId is a database ID from FeatureTreeSelect
        featureIdsToCheck.add(selectedFeatureId)
        
        // Also get the featureId for this selected feature
        const selectedFeature = features.value.find(f => f.id === selectedFeatureId)
        if (selectedFeature && selectedFeature.featureId) {
          featureFeatureIdsToCheck.add(selectedFeature.featureId)
        }
        
        // Only include sub-tasks if the checkbox is checked
        if (includeSubTasks.value) {
          // Find the feature and add all its sub-tasks recursively
          const findSubTasks = (featureId) => {
            const feature = features.value.find(f => f.id === featureId)
            if (feature) {
              // Add direct sub-tasks from sub_task_ids array
              if (feature.sub_task_ids && feature.sub_task_ids.length > 0) {
                feature.sub_task_ids.forEach(subTaskId => {
                  featureIdsToCheck.add(subTaskId)
                  // Also add featureId if available
                  const subTaskFeature = features.value.find(f => f.id === subTaskId)
                  if (subTaskFeature && subTaskFeature.featureId) {
                    featureFeatureIdsToCheck.add(subTaskFeature.featureId)
                  }
                  // Recursively find nested sub-tasks
                  findSubTasks(subTaskId)
                })
              }
              
              // Also check for sub-tasks using parent_id relationship
              const childFeatures = features.value.filter(f => 
                (f.parent_id === featureId || f.parentId === featureId)
              )
              childFeatures.forEach(child => {
                featureIdsToCheck.add(child.id)
                if (child.featureId) {
                  featureFeatureIdsToCheck.add(child.featureId)
                }
                findSubTasks(child.id) // Recursively find nested sub-tasks
              })
            }
          }
          
          findSubTasks(selectedFeatureId)
        }
      })
      
      // Check if bug is linked to any of the selected features (and optionally their sub-tasks)
      // Bug's feature_ids might contain either database IDs or featureIds, so check both
      const hasMatchingFeature = bugFeatureIds.some(bugFeatureId => {
        // Check if bugFeatureId matches database ID
        if (featureIdsToCheck.has(bugFeatureId)) {
          return true
        }
        // Check if bugFeatureId matches featureId (F00051, etc.)
        if (featureFeatureIdsToCheck.has(bugFeatureId)) {
          return true
        }
        // Also check if bugFeatureId is a database ID that matches any selected feature's database ID
        // (in case bug has database IDs but we're comparing with featureIds)
        const bugFeature = features.value.find(f => f.id === bugFeatureId || f.featureId === bugFeatureId)
        if (bugFeature) {
          return featureIdsToCheck.has(bugFeature.id) || featureFeatureIdsToCheck.has(bugFeature.featureId)
        }
        return false
      })
      
      // Debug logging for feature filter matching
      if (filters.value.feature.length > 0 && bugFeatureIds.length > 0) {
        console.log('🔍 Feature Filter Debug:', {
          bugId: bug.bugId || bug.id,
          bugTitle: bug.title,
          bugFeatureIds: bugFeatureIds,
          selectedFeatureIds: Array.from(featureIdsToCheck),
          selectedFeatureFeatureIds: Array.from(featureFeatureIdsToCheck),
          hasMatchingFeature
        })
      }
      
      if (!hasMatchingFeature) {
        return false
      }
    }
    
    return true
  })
  
  // Sort by creation timestamp in descending order (newest first)
  return result.sort((a, b) => {
    const dateA = a.created_at || a.createdAt
    const dateB = b.created_at || b.createdAt
    let timeA = 0
    let timeB = 0
    
    if (dateA) {
      if (dateA instanceof Date) {
        timeA = dateA.getTime()
      } else if (typeof dateA === 'string') {
        const parsed = new Date(dateA).getTime()
        timeA = isNaN(parsed) ? 0 : parsed
      } else if (typeof dateA === 'number') {
        timeA = dateA
      }
    }
    
    if (dateB) {
      if (dateB instanceof Date) {
        timeB = dateB.getTime()
      } else if (typeof dateB === 'string') {
        const parsed = new Date(dateB).getTime()
        timeB = isNaN(parsed) ? 0 : parsed
      } else if (typeof dateB === 'number') {
        timeB = dateB
      }
    }
    
    return timeB - timeA // Descending order (newest first)
  })
})

// Paginated Bugs
const paginatedBugs = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  const end = start + pageSize.value
  const result = filteredBugs.value.slice(start, end)
  // Debug logging (can be removed later)
  console.log('Pagination Debug:', {
    pageSize: pageSize.value,
    currentPage: currentPage.value,
    filteredBugsCount: filteredBugs.value.length,
    start,
    end,
    paginatedCount: result.length
  })
  return result
})

// Dynamic max-height for table wrapper based on page size
// For large page sizes, don't limit height to allow all rows to be visible
const tableMaxHeight = computed(() => {
  if (pageSize.value >= 100) {
    return undefined // No height limit for 100+ rows - let them all show
  } else if (pageSize.value >= 50) {
    return '800px'
  }
  return '600px'
})

// Data Table Columns
const dataTableColumns = computed(() => [
  {
    id: 'bugId',
    accessorKey: 'bugId',
    header: 'Bug ID',
    size: 100,
    enableResizing: true,
    enableSorting: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      if (!bug.bugId) {
        return h('span', {
          class: 'text-sm text-gray-400 dark:text-gray-500'
        }, '—')
      }
      return h('span', {
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer hover:underline',
        onClick: () => viewBug(bug),
        title: 'Click to view'
      }, bug.bugId)
    }
  },
  {
    id: 'title',
    accessorKey: 'title',
    header: 'Title',
    size: 300,
    enableResizing: true,
    enableSorting: true,
    meta: {
      align: 'left',
      headerAlign: 'left'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      const isDeleted = bug.isDeleted || bug.deleted_at
      const title = bug.title || 'Untitled Bug'
      
      return h('div', { class: 'flex items-center gap-2 min-w-0' }, [
        h('div', { class: 'flex-1 min-w-0' }, [
          h(HLPopover, {
            trigger: 'hover',
            placement: 'top',
            'show-arrow': false
          }, {
            trigger: () => h('div', {
              class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium line-clamp-2 hover:underline transition-colors cursor-pointer',
              onClick: () => viewBug(bug),
              title: 'Click to view'
            }, title),
            default: () => h('div', {
              class: 'text-sm bg-gray-900 text-white p-2 rounded max-w-xs'
            }, title)
          })
        ]),
        isDeleted ? h(HLTag, {
          id: `bug-deleted-${bug.id}`,
          color: 'gray',
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false,
          class: 'flex-shrink-0'
        }, { default: () => 'Deleted' }) : null
      ])
    }
  },
  {
    id: 'project',
    accessorKey: 'project_id',
    header: 'Project',
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      const projectName = getProjectName(bug.project_id)
      return h('div', { class: 'text-sm text-gray-600 dark:text-gray-400 text-center' }, projectName)
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          id: `bug-status-${bug.id}`,
          color: getHLTagColor(getStatusColor(bug.status)),
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, { default: () => getStatusLabel(bug.status) })
      ])
    }
  },
  {
    id: 'priority',
    accessorKey: 'priority',
    header: 'Priority',
    size: 120,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          id: `bug-priority-${bug.id}`,
          color: getHLTagColor(getPriorityColor(bug.priority)),
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, { default: () => getPriorityLabel(bug.priority) })
      ])
    }
  },
  {
    id: 'severity',
    accessorKey: 'severity',
    header: 'Severity',
    size: 120,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          id: `bug-severity-${bug.id}`,
          color: getHLTagColor(getSeverityColor(bug.severity)),
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, { default: () => getSeverityLabel(bug.severity) })
      ])
    }
  },
  {
    id: 'assignedTo',
    accessorKey: 'assigned_to',
    header: 'Assigned To',
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'text-sm text-gray-600 dark:text-gray-400 text-center' }, getAssigneeName(bug.assigned_to))
    }
  },
  {
    id: 'createdAt',
    accessorKey: 'created_at',
    header: 'Created',
    size: 120,
    enableResizing: true,
    enableSorting: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'text-sm text-gray-600 dark:text-gray-400 text-center' }, formatDate(bug.created_at))
    }
  },
  {
    id: 'actions',
    accessorKey: 'id',
    header: 'Actions',
    size: 180,
    enableResizing: true,
    meta: {
      align: 'right',
      headerAlign: 'right'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      const isDeleted = bug.isDeleted || bug.deleted_at
      
      const buttons = []
      
      // View button (always visible)
      buttons.push(h(HLButton, {
        id: `bug-view-${bug.id}`,
        onClick: () => viewBug(bug),
        variant: 'text',
        color: ACTION_ICON_COLORS.view,
        size: 'sm',
        title: 'View'
      }, {
        iconLeft: () => h(Icon, { name: 'eye', size: 'sm' })
      }))
      
      if (!isDeleted) {
        // Edit button
        if (can.editBugs()) {
          buttons.push(h(HLButton, {
            id: `bug-edit-${bug.id}`,
            onClick: () => editBug(bug),
            variant: 'text',
            color: ACTION_ICON_COLORS.edit,
            size: 'sm',
            title: 'Edit'
          }, {
            iconLeft: () => h(Icon, { name: 'edit', size: 'sm' })
          }))
        }
        
        // Clone button
        if (can.createBugs()) {
          buttons.push(h(HLButton, {
            id: `bug-clone-${bug.id}`,
            onClick: () => cloneSingleBug(bug.id),
            variant: 'text',
            color: ACTION_ICON_COLORS.clone,
            size: 'sm',
            title: 'Clone'
          }, {
            iconLeft: () => h(Icon, { name: 'copy', size: 'sm' })
          }))
        }
        
        // Delete button
        if (can.deleteBugs()) {
          buttons.push(h(HLButton, {
            id: `bug-delete-${bug.id}`,
            onClick: () => deleteBug(bug.id),
            variant: 'text',
            color: ACTION_ICON_COLORS.delete,
            size: 'sm',
            title: 'Delete'
          }, {
            iconLeft: () => h(Icon, { name: 'trash', size: 'sm' })
          }))
        }
      } else {
        // Restore button (for deleted bugs)
        if (can.deleteBugs()) {
          buttons.push(h(HLButton, {
            id: `bug-restore-${bug.id}`,
            onClick: () => restoreBug(bug.id),
            variant: 'text',
            color: 'green',
            size: 'sm',
            title: 'Restore'
          }, {
            iconLeft: () => h(Icon, { name: 'restore', size: 'sm' })
          }))
        }
      }
      
      return h('div', { class: 'flex items-center justify-end gap-1' }, buttons)
    }
  }
])

// Helper function to map extracted color names to HLTag-compatible colors
const getHLTagColor = (colorName) => {
  const colorMap = {
    'green': 'success',
    'red': 'error',
    'blue': 'primary',
    'orange': 'warning',
    'yellow': 'warning',
    'purple': 'purple',
    'cyan': 'primary',
    'indigo': 'purple',
    'gray': 'gray'
  }
  return colorMap[colorName] || 'gray'
}

// OPTIMIZATION: SessionStorage caching
const CACHE_KEY_PREFIX = 'bugs_list_'
const CACHE_DURATION = 10000 // 10 seconds (reduced to prevent stale search results)

const getCache = (key) => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY_PREFIX + key)
    if (cached) {
      const { data, timestamp } = JSON.parse(cached)
      const now = Date.now()
      if ((now - timestamp) < CACHE_DURATION) {
        return data
      }
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null
}

const setCache = (key, data) => {
  try {
    sessionStorage.setItem(CACHE_KEY_PREFIX + key, JSON.stringify({
      data,
      timestamp: Date.now()
    }))
  } catch (e) {
    // Ignore cache errors (quota exceeded, etc.)
  }
}

const clearCache = (key = null) => {
  try {
    if (key) {
      sessionStorage.removeItem(CACHE_KEY_PREFIX + key)
    } else {
      // Clear all bugs-related cache
      Object.keys(sessionStorage).forEach(k => {
        if (k.startsWith(CACHE_KEY_PREFIX)) {
          sessionStorage.removeItem(k)
        }
      })
    }
  } catch (e) {
    // Ignore cache errors
  }
}

// Methods
const loadBugs = async () => {
  loading.value = true
  try {
    // Load both active and deleted bugs
    const [activeData, deletedData] = await Promise.all([
      bugService.getAll().then(data => (data || []).filter(b => !b.is_deleted)),
      bugService.getDeletedBugs ? bugService.getDeletedBugs() : Promise.resolve([])
    ])
    
    // Combine and normalize the data
    const activeBugs = activeData.map(bug => ({
      ...bug,
      is_deleted: false,
      isDeleted: false
    }))
    
    const deletedBugs = deletedData.map(bug => ({
      ...bug,
      is_deleted: true,
      isDeleted: true
    }))
    
    // Combine and sort by creation timestamp in descending order (newest first)
    const allBugs = [...activeBugs, ...deletedBugs]
    allBugs.sort((a, b) => {
      const dateA = a.created_at || a.createdAt
      const dateB = b.created_at || b.createdAt
      let timeA = 0
      let timeB = 0
      
      if (dateA) {
        if (dateA instanceof Date) {
          timeA = dateA.getTime()
        } else if (typeof dateA === 'string') {
          const parsed = new Date(dateA).getTime()
          timeA = isNaN(parsed) ? 0 : parsed
        } else if (typeof dateA === 'number') {
          timeA = dateA
        }
      }
      
      if (dateB) {
        if (dateB instanceof Date) {
          timeB = dateB.getTime()
        } else if (typeof dateB === 'string') {
          const parsed = new Date(dateB).getTime()
          timeB = isNaN(parsed) ? 0 : parsed
        } else if (typeof dateB === 'number') {
          timeB = dateB
        }
      }
      
      return timeB - timeA // Descending order (newest first)
    })
    
    bugs.value = allBugs
    
    // Cache the results
    setCache('bugs', bugs.value)
  } catch (error) {
    console.error('❌ Error loading bugs:', error)
    toast.error('Failed to load bugs')
    bugs.value = []
  } finally {
    loading.value = false
  }
}

const loadUsers = async () => {
  try {
    // Check cache first
    const cached = getCache('users')
    if (cached) {
      users.value = cached
      return
    }
    
    const data = await userService.getAll()
    users.value = data || []
    
    // Cache the results
    setCache('users', users.value)
  } catch (error) {
    // Silent fail
  }
}

const loadProjects = async () => {
  try {
    // Check cache first
    const cached = getCache('projects')
    if (cached) {
      projects.value = cached
      return
    }
    
    const data = await projectService.getAll()
    // Filter out deleted projects
    projects.value = (data || []).filter(p => !p.is_deleted)
    
    // Cache the results
    setCache('projects', projects.value)
  } catch (error) {
    // Silent fail
  }
}

const loadFeatures = async () => {
  try {
    // Check cache first
    const cached = getCache('features')
    if (cached) {
      features.value = cached
      console.log('✅ Loaded features from cache:', features.value.length)
      return
    }
    
    const { featureService } = await import('@/database/services')
    // Load all features using getAll (inherited from BaseService)
    const allFeatures = await featureService.getAll()
    
    // Normalize features: ensure all have 'id' field (some might have '_id' from database)
    // Filter out deleted features
    features.value = (allFeatures || [])
      .filter(f => !f.is_deleted && !f.isDeleted)
      .map(f => ({
        ...f,
        id: f.id || f._id || f.featureId || `feature_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        parent_id: f.parent_id || f.parentId || null,
        sub_task_ids: f.sub_task_ids || []
      }))
    
    console.log('✅ Loaded features:', features.value.length)
    console.log('📋 Sample features:', features.value.slice(0, 3).map(f => ({
      id: f.id,
      featureId: f.featureId,
      title: f.title,
      parent_id: f.parent_id,
      sub_task_ids: f.sub_task_ids
    })))
    
    // Cache the results
    setCache('features', features.value)
  } catch (error) {
    console.error('❌ Error loading features:', error)
    features.value = []
  }
}

const refreshBugs = async () => {
  // Clear cache to force fresh data
  clearCache('bugs')
  await loadBugs()
}

const clearAllFilters = () => {
  filters.value = {
    search: '',
    project: [],
    status: [],
    priority: [],
    severity: [],
    assignedTo: [],
    feature: []
  }
  currentPage.value = 1
}

const handleIncludeDeletedToggle = (value) => {
  showDeleted.value = value
  currentPage.value = 1
}

const handlePageChange = (page) => {
  currentPage.value = page
}

const tableInstance = ref(null)
const handlePerPageChange = (perPage) => {
  // Update our pageSize first (this will update paginatedBugs computed)
  const newPageSize = Number(perPage)
  console.log('handlePerPageChange called:', { perPage, newPageSize, currentPageSize: pageSize.value })
  
  pageSize.value = newPageSize
  currentPage.value = 1 // Reset to first page
  
  // Force reactivity update
  nextTick(() => {
    console.log('After nextTick:', { pageSize: pageSize.value, paginatedBugsCount: paginatedBugs.value.length })
  })
  
  // Try to update table's internal pagination if the method exists
  // Note: This is optional - our manual pagination should work regardless
  if (tableInstance.value?.table?.setPageSize) {
    try {
      tableInstance.value.table.setPageSize(newPageSize)
    } catch (error) {
      // Ignore if method doesn't exist or fails
      console.warn('Table setPageSize method not available:', error)
    }
  }
}

const createBug = (e) => {
  // Show project selector modal
  if (projects.value.length === 0) {
    toast.error('No projects available. Please create a project first.')
    return
  }
  
  if (projects.value.length === 1) {
    // If only one project, go directly to create bug for that project
    const path = `/projects/${projects.value[0].id}/bugs/create`
    if (e && (e.metaKey || e.ctrlKey)) {
      window.open(path, '_blank')
  } else {
      router.push(path)
    }
  } else {
    // Show project selector modal (can't open modal in new tab, so just show modal)
    showProjectSelector.value = true
  }
}

const handleProjectSelected = (project) => {
  showProjectSelector.value = false
  router.push(`/projects/${project.id}/bugs/create`)
}

const viewBug = (bug) => {
  router.push(`/projects/${bug.project_id}/bugs/${bug.id}`)
}

const editBug = (bug) => {
  router.push(`/projects/${bug.project_id}/bugs/${bug.id}/edit`)
}

const deleteBug = (bugId) => {
  selectedBugId.value = bugId
  showDeleteModal.value = true
}

const confirmDeleteBug = async () => {
  try {
    await bugService.delete(selectedBugId.value)
    // Clear cache to ensure fresh data
    clearCache('bugs')
    toast.success('Bug deleted successfully')
    await loadBugs()
    showDeleteModal.value = false
    selectedBugId.value = null
  } catch (error) {
    toast.error('Failed to delete bug')
  }
}

const restoreBug = (bugId) => {
  selectedBugId.value = bugId
  showRestoreModal.value = true
}

const confirmRestoreBug = async () => {
  try {
    await bugService.restore(selectedBugId.value)
    // Clear cache to ensure fresh data
    clearCache('bugs')
    toast.success('Bug restored successfully')
    await loadBugs()
    showRestoreModal.value = false
    selectedBugId.value = null
  } catch (error) {
    toast.error('Failed to restore bug')
  }
}

const cloneSingleBug = (bugId) => {
  selectedSingleBugId.value = bugId
  showSingleCloneModal.value = true
}

const confirmSingleCloneBug = async () => {
  cloning.value = true
  try {
    const currentUser = authStore?.user
    
    // Clone the bug
    await bugService.cloneBug(selectedSingleBugId.value, currentUser?.id)
    
    // Clear cache to ensure fresh data
    clearCache('bugs')
    toast.success('Bug cloned successfully')
    await loadBugs()
    showSingleCloneModal.value = false
    selectedSingleBugId.value = null
  } catch (error) {
    const errorMessage = error?.response?.data?.error || error?.message || 'Failed to clone bug'
    toast.error(errorMessage)
  } finally {
    cloning.value = false
  }
}

// Helper Functions
const getProjectName = (projectId) => {
  if (!projectId) return 'Unknown'
  const project = projects.value.find(p => p.id === projectId)
  return project?.name || 'Unknown'
}

const getAssigneeName = (userId) => {
  if (!userId) return 'Unassigned'
  const user = users.value.find(u => u.id === userId)
  return user?.name || user?.email || 'Unknown'
}

const getStatusLabel = (status) => {
  const option = BUG_STATUS_OPTIONS.find(o => o.value === status)
  return option ? option.label : status
}

const getPriorityLabel = (priority) => {
  const option = BUG_PRIORITY_OPTIONS.find(o => o.value === priority)
  return option ? option.label : priority
}

const getSeverityLabel = (severity) => {
  const option = BUG_SEVERITY_OPTIONS.find(o => o.value === severity)
  return option ? option.label : severity
}

const getStatusColor = (status) => {
  const colors = {
    open: 'red',              // Red - needs attention
    in_progress: 'blue',      // Blue - being worked on
    resolved: 'green',        // Green - fixed
    closed: 'gray',           // Gray - completed/archived
    reopened: 'orange',       // Orange - needs re-attention
    blocked: 'purple',        // Purple - blocked/waiting
    not_a_bug: 'yellow',      // Yellow - not a bug
    setup_issue: 'cyan'       // Cyan - setup/config issue
  }
  return colors[status] || 'red'
}

const getPriorityColor = (priority) => {
  const colors = {
    critical: 'red',
    high: 'orange',
    medium: 'yellow',
    low: 'gray'
  }
  return colors[priority] || 'yellow'
}

const getSeverityColor = (severity) => {
  const colors = {
    critical: 'red',
    major: 'orange',
    moderate: 'yellow',
    minor: 'blue',
  }
  return colors[severity] || 'yellow'
}

// Watch filters and reset pagination
watch(filters, () => {
  currentPage.value = 1
}, { deep: true })

// Watch includeSubTasks and reset pagination
watch(includeSubTasks, () => {
  currentPage.value = 1
})

// Watch search filter specifically - if search is active and cache is stale, refresh data
watch(() => filters.value.search, async (newSearch) => {
  // If user is searching and cache might be stale, refresh
  if (newSearch && newSearch.trim()) {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY_PREFIX + 'bugs')
      if (cached) {
        const { timestamp } = JSON.parse(cached)
        const age = Date.now() - timestamp
        // If cache is older than 10 seconds, refresh in background
        if (age > 10000) {
          loadBugs().catch(err => {
            console.error('Background refresh failed:', err)
          })
        }
      }
    } catch (e) {
      // Ignore errors
    }
  }
})

// Lifecycle
onMounted(async () => {
  // OPTIMIZATION: Try to load from cache first
  const cachedBugs = getCache('bugs')
  const cachedProjects = getCache('projects')
  const cachedUsers = getCache('users')
  
  if (cachedBugs && cachedProjects && cachedUsers) {
    bugs.value = cachedBugs
    projects.value = cachedProjects
    users.value = cachedUsers
    loading.value = false
    return
  }
  
  // Load fresh data (will cache after loading)
  await Promise.all([
    loadProjects(),
    loadUsers(),
    loadBugs()
  ])
  
  // Load features after projects are loaded
  await loadFeatures()
})

// OPTIMIZATION: Check cache when component is activated (tab switching)
onActivated(async () => {
  // Always refresh bugs when component is activated to ensure fresh data
  // This prevents search issues with stale cached data
  const cachedBugs = getCache('bugs')
  const cachedProjects = getCache('projects')
  const cachedUsers = getCache('users')
  const cachedFeatures = getCache('features')
  
  // Use cache only if we have no bugs loaded (initial load)
  if (cachedBugs && cachedProjects && cachedUsers && bugs.value.length === 0) {
    bugs.value = cachedBugs
    projects.value = cachedProjects
    users.value = cachedUsers
    if (cachedFeatures) {
      features.value = cachedFeatures
    }
    loading.value = false
  }
  
  // Always refresh bugs in background to ensure data is up-to-date
  // This ensures search works with latest data
  if (bugs.value.length === 0) {
    await loadBugs()
  } else {
    // Refresh in background without blocking UI
    loadBugs().catch(err => {
      console.error('Background refresh failed:', err)
    })
  }
  
  if (projects.value.length === 0) await loadProjects()
  if (users.value.length === 0) loadUsers()
  if (features.value.length === 0) await loadFeatures()
})
</script>

<style scoped>
/* HLDataTable styling for Bugs */
:deep(.bugs-data-table) {
  width: 100% !important;
  max-width: none !important;
  background-color: white !important;
}

:deep(.dark .bugs-data-table) {
  background-color: rgb(31 41 55) !important;
}

:deep(.bugs-data-table .hr-data-table-wrapper) {
  overflow-x: auto;
  width: 100% !important;
  max-width: none !important;
  border-left: none !important;
  border-right: none !important;
  border-bottom: none !important;
}

:deep(.bugs-data-table .hr-data-table) {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100% !important;
  max-width: none !important;
  min-width: 100%;
  border-left: none !important;
  border-right: none !important;
  border-bottom: none !important;
}

:deep(.hr-data-table th) {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.05em;
  color: rgb(75 85 99) !important;
  background-color: rgb(249 250 251) !important;
  padding: 1.25rem 1rem;
  white-space: normal;
  word-wrap: break-word;
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: 1px solid rgb(229 231 235) !important;
}

:deep(.dark .hr-data-table th) {
  color: rgb(209 213 219) !important;
  background-color: rgb(31 41 55) !important;
  border-bottom: 1px solid rgb(55 65 81) !important;
}

:deep(.hr-data-table td) {
  padding: 1.25rem 1rem;
  vertical-align: middle;
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: 1px solid rgb(243 244 246) !important;
}

:deep(.dark .hr-data-table td) {
  border-bottom: 1px solid rgb(55 65 81) !important;
}

:deep(.hr-data-table th:first-child) {
  padding-left: 2rem !important;
}

:deep(.hr-data-table td:first-child) {
  padding-left: 2rem !important;
}

:deep(.hr-data-table th:last-child) {
  padding-right: 2rem !important;
}

:deep(.hr-data-table td:last-child) {
  padding-right: 2rem !important;
}

:deep(.hr-data-table tbody tr) {
  height: auto;
  min-height: 72px;
  line-height: 1.5;
}

:deep(.hr-data-table tbody tr:last-child td) {
  border-bottom: none !important;
}

:deep(.hr-data-table tbody tr:hover) {
  background-color: rgb(249 250 251) !important;
}

:deep(.dark .hr-data-table tbody tr:hover) {
  background-color: rgb(31 41 55) !important;
}

/* Max height for approximately 10 rows */
:deep(.bugs-data-table .hr-data-table-wrapper) {
  max-height: 720px;
  overflow-y: auto;
}
</style>
